import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  MdReceipt, MdPerson, MdCalendarToday, MdOutlinePayment, 
  MdArrowBack, MdAccountBalanceWallet, MdErrorOutline, MdPhoneIphone, MdNumbers 
} from "react-icons/md";
import { baseurl } from "../../baseurl";

const ElectricityBillDetail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const [billData, setBillData] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [isPaying, setIsPaying] = useState(false);
  const [mobileNumber, setMobileNumber] = useState("");
  
  const guestUserId = localStorage.getItem('guestUserId');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawData = params.get("data");

    if (rawData) {
      try {
        const decodedData = JSON.parse(decodeURIComponent(rawData));
        setBillData(decodedData);
      } catch (err) {
        console.error("Failed to parse bill data", err);
      }
    }

    if (guestUserId) {
      fetchBalance();
    }
  }, [location, guestUserId]);

  const fetchBalance = () => {
    axios.get(`${baseurl}/getWalletBalance/${guestUserId}`)
      .then(res => setWalletBalance(parseFloat(res.data.balance) || 0))
      .catch(err => console.error("Wallet Error", err));
  };

  const handleProceedToPay = async () => {
    if (!guestUserId) return;
    
    if (!mobileNumber || mobileNumber.length !== 10) {
      alert("Please enter a valid 10-digit mobile number for receipts.");
      return;
    }

    // Explicitly mapping all data from the URL payload for the backend
    const paymentPayload = {
      guest_id: guestUserId,
      biller_id: billData.biller_id, // "MAHA00000MAH01"
      amount: billData.amount, // 130
      reference_id: billData.reference_id, // "ABHDY12345..."
      customerParams: billData.customerParams, // [{name: "Consumer No", value: "410031986544"}]
      mobile_number: mobileNumber,
    };

    console.log("Final Payment Payload Sent to Backend:", paymentPayload);

    setIsPaying(true);
    try {
      const res = await axios.post(`${baseurl}/payElectricityBillGuest`, paymentPayload);
      if (res.data.status === "SUCCESS") {
        alert("Payment Successful!");
        navigate("/booking");
      } else {
        alert(res.data.message || "Payment Failed");
      }
    } catch (err) {
      console.error("Payment Error:", err);
      alert("An error occurred during payment.");
    } finally {
      setIsPaying(false);
    }
  };

  if (!billData) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status"></div>
        <p className="mt-3 text-muted">Loading bill details...</p>
      </div>
    );
  }

  const hasInsufficientBalance = walletBalance < billData.amount;

  return (
    <div className="container py-4 animate-fade-in" style={{ maxWidth: "500px" }}>
      <button onClick={() => navigate(-1)} className="btn btn-link text-decoration-none text-dark p-0 mb-3 d-flex align-items-center">
        <MdArrowBack className="me-1" /> Back
      </button>

      <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: "15px" }}>
        {/* Header Section */}
        <div className="p-4 text-center text-white" style={{ background: "linear-gradient(135deg, #166070 0%, #2a8a9d 100%)" }}>
          <div className="bg-white d-inline-block rounded-circle p-3 mb-2 shadow-sm">
            <MdReceipt size={30} style={{ color: "#166070" }} />
          </div>
          <h5 className="mb-0 fw-bold">Electricity Bill Details</h5>
          <p className="x-small opacity-75 mb-0 mt-1">Ref ID: {billData.reference_id}</p>
        </div>

        {/* Amount & Wallet Info */}
        <div className="bg-light p-4 text-center border-bottom">
          <span className="text-muted small text-uppercase fw-bold d-block mb-1">Total Payable Amount</span>
          <h2 className="fw-bold mb-2" style={{ color: "#166070" }}>₹{billData.amount}</h2>
          
          {guestUserId ? (
            <div className={`d-inline-flex align-items-center px-3 py-1 rounded-pill ${hasInsufficientBalance ? 'bg-danger-subtle text-danger' : 'bg-success-subtle text-success'} `} style={{ fontSize: '12px' }}>
              <MdAccountBalanceWallet className="me-1" /> Wallet Balance: ₹{walletBalance.toFixed(2)}
            </div>
          ) : (
            <div className="badge bg-warning text-dark px-3 py-2"><MdErrorOutline className="me-1" /> Login required to pay</div>
          )}
        </div>

        <div className="p-4">
          <div className="row g-3">
            {/* Customer Name */}
            <div className="col-12 d-flex align-items-start border-bottom pb-2">
              <div className="icon-box me-3"><MdPerson size={20} /></div>
              <div>
                <label className="text-muted x-small text-uppercase d-block fw-bold">Customer Name</label>
                <span className="fw-medium text-dark">{billData.name}</span>
              </div>
            </div>

            {/* Consumer Number Mapping */}
            {billData.customerParams?.map((param, i) => (
              <div key={i} className="col-12 d-flex align-items-start border-bottom pb-2 mt-2">
                <div className="icon-box me-3"><MdNumbers size={20} /></div>
                <div>
                  <label className="text-muted x-small text-uppercase d-block fw-bold">{param.name}</label>
                  <span className="fw-medium text-dark">{param.value}</span>
                </div>
              </div>
            ))}

            {/* Bill Info Grid */}
            <div className="col-6 d-flex align-items-start mt-3">
              <div className="icon-box me-3"><MdReceipt size={18} /></div>
              <div>
                <label className="text-muted x-small text-uppercase d-block fw-bold">Bill No.</label>
                <span className="fw-medium text-dark">{billData.billNumber}</span>
              </div>
            </div>

            <div className="col-6 d-flex align-items-start mt-3">
              <div className="icon-box me-3"><MdCalendarToday size={18} /></div>
              <div>
                <label className="text-muted x-small text-uppercase d-block fw-bold">Bill Date</label>
                <span className="fw-medium text-dark">{billData.billDate}</span>
              </div>
            </div>
            
            <div className="col-12 d-flex align-items-start mt-3">
              <div className="icon-box me-3 text-danger"><MdCalendarToday size={18} /></div>
              <div>
                <label className="text-muted x-small text-uppercase d-block fw-bold text-danger">Due Date</label>
                <span className="fw-medium text-danger">{billData.dueDate}</span>
              </div>
            </div>
          </div>

          <hr className="my-4" />

          {/* Phone Number Input */}
          <div className="mb-4">
            <label className="fw-bold x-small text-muted text-uppercase mb-2">Notification Mobile Number</label>
            <div className="input-group shadow-sm border rounded-2 overflow-hidden">
              <span className="input-group-text bg-white border-0"><MdPhoneIphone /></span>
              <input 
                type="number" 
                className="form-control border-0 shadow-none py-2" 
                placeholder="Enter 10 digit mobile number"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value.slice(0, 10))}
              />
            </div>
          </div>

          {/* Action Buttons */}
          {!guestUserId ? (
            <div className="alert alert-info text-center border-0 py-3 shadow-sm" style={{ borderRadius: '10px' }}>
              <p className="small mb-2 fw-bold">Please login to proceed with payment.</p>
              <button className="btn btn-primary btn-sm px-4 fw-bold" onClick={() => navigate('/login')}>Login Now</button>
            </div>
          ) : hasInsufficientBalance ? (
            <div className="alert alert-danger text-center border-0 py-3 shadow-sm" style={{ borderRadius: '10px' }}>
              <p className="small mb-2 fw-bold">Insufficient wallet balance!</p>
              <button className="btn btn-danger btn-sm px-4 fw-bold" onClick={() => navigate('/add-money')}>Top-up Pocket</button>
            </div>
          ) : (
            <button 
              className="btn w-100 py-3 text-white fw-bold shadow d-flex align-items-center justify-content-center"
              style={{ background: "#166070", borderRadius: "10px", fontSize: "1.1rem" }}
              onClick={handleProceedToPay}
              disabled={isPaying}
            >
              {isPaying ? (
                <><span className="spinner-border spinner-border-sm me-2"></span> PAYING...</>
              ) : (
                <><MdOutlinePayment className="me-2" /> PROCEED TO PAY</>
              )}
            </button>
          )}

          {/* Additional Info Grid (DTC Code, Bill Type, etc.) */}
          {billData.additionalInfo && (
            <div className="mt-4 pt-3 border-top">
              <h6 className="x-small text-uppercase fw-bold text-muted mb-3">Billing Specifications</h6>
              <div className="row g-2">
                {billData.additionalInfo.map((info, idx) => (
                  <div key={idx} className="col-6">
                    <div className="p-2 rounded-2 bg-light border border-light-subtle">
                      <label className="d-block x-small text-muted mb-0">{info.infoName}</label>
                      <span className="fw-bold text-dark" style={{ fontSize: "11px" }}>{info.infoValue}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .x-small { font-size: 11px; }
        .icon-box { color: #166070; background: rgba(22, 96, 112, 0.08); padding: 8px; border-radius: 8px; }
        .bg-success-subtle { background-color: #d1e7dd; color: #0f5132 !important; }
        .bg-danger-subtle { background-color: #f8d7da; color: #842029 !important; }
        input[type=number]::-webkit-inner-spin-button, 
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        .animate-fade-in { animation: fadeIn 0.5s ease-in-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default ElectricityBillDetail;